// Copyright 2025, University of Maryland, All Rights Reserved
import { AnalysisHeader } from './AnalysisHeader';
import { ExecutiveSummary } from './ExecutiveSummary';
import { ConceptsCovered } from './ConceptsCovered';
import { LearningObjectives } from './LearningObjectives';
import { LearningObjectivesCoverageChart } from './LearningObjectivesCoverageChart';
import { LearningObjectivesPointsChart } from './LearningObjectivesPointsChart';
import { ProblemTopicBreakdown } from './ProblemTopicBreakdown';
import { ProblemAnalysis } from './ProblemAnalysis';
import { PedagogicalApproach } from './PedagogicalApproach';
import { OverallAssessment } from './OverallAssessment';
import { RawDataDebug } from './RawDataDebug';
import { CLOHeatmap } from './CLOHeatmap';
import { PrerequisiteGapAnalysis } from './PrerequisiteGapAnalysis';
import { PrerequisiteReadiness } from './PrerequisiteReadiness';
import { PLOHeatmap } from './PLOHeatmap';
import DynamicRenderer from '../DynamicRenderer';

interface AnalysisReportProps {
  analysisData: any;
}

/**
 * Main component that orchestrates rendering of the complete analysis report
 */
export function AnalysisReport({ analysisData }: AnalysisReportProps) {
  if (!analysisData) return null;

  // Handle both direct analysis results and completed document analysis
  const data = analysisData.result?.analysis_data || analysisData;
  const documentType = analysisData.document_type || analysisData.documentType;

  // Unstructured mode: document_type "other" returns free-form text instead of structured JSON
  if (data.is_structured === false) {
    return <DynamicRenderer data={data.raw_response} />;
  }
  
  // Extract filenames array from document_metadata if available
  const filenames = data.document_metadata?.filenames || [];
  const documentTypes = data.document_metadata?.document_types || [];
  const documentCount = analysisData.documentCount || (filenames.length > 1 ? filenames.length : undefined);

  // New multi-prerequisite readiness result (single-call prerequisite check).
  // Detected by shape so it renders regardless of the passed document_type.
  // NOTE: intentionally NOT wrapped in .heatmap-print-wrapper — that routes content
  // through the landscape one-page heatmap scaler. This is a normal tall document,
  // so it prints WYSIWYG (portrait, colors) like a regular analysis report.
  if (data.requiring_clo_readiness && data.readiness_rating) {
    return (
      <div>
        <PrerequisiteReadiness
          requiring_clo_readiness={data.requiring_clo_readiness}
          readiness_rating={data.readiness_rating}
          readiness_explanation={data.readiness_explanation || ''}
          summary={data.summary}
          prerequisite_labels={
            data.prerequisite_courses?.map((c: any) => c.label)
            || data.document_metadata?.labels?.slice(0, -1)
            || []
          }
          requiring_label={
            data.requiring_course?.label
            || data.document_metadata?.labels?.slice(-1)?.[0]
          }
        />
      </div>
    )
  }

  // CLO Comparison / Prerequisite Check
  if (documentType === 'clo_comparison' || documentType === 'prerequisite_check') {
    if (data.gap_analysis && data.readiness_rating) {
      return (
        <div className="heatmap-print-wrapper">
          <PrerequisiteGapAnalysis
            gap_analysis={data.gap_analysis}
            readiness_rating={data.readiness_rating}
            readiness_explanation={data.readiness_explanation}
            prerequisite_label={data.document_metadata?.labels?.[0] || data.course_instances?.[0]?.label}
            requiring_label={data.document_metadata?.labels?.[1] || data.course_instances?.[1]?.label}
          />
        </div>
      )
    }
    return (
      <div className="heatmap-print-wrapper">
        <CLOHeatmap canonical_clos={data.canonical_clos || []} course_instances={data.course_instances || []} summary={data.summary} />
      </div>
    )
  }

  // PLO Generation / PLO Mapping
  if (documentType === 'plo_generation' || documentType === 'plo_mapping') {
    return (
      <div className="heatmap-print-wrapper">
        <PLOHeatmap program_learning_outcomes={data.program_learning_outcomes || []} course_instances={data.course_instances || []} summary={data.summary} />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <AnalysisHeader
        filename={analysisData.filename}
        originalFilename={analysisData.original_filename}
        filenames={filenames}
        documentTypes={documentTypes}
        documentCount={documentCount}
        documentType={documentType}
        llmModel={data.document_metadata?.llm_model}
        llmCost={data.document_metadata?.llm_cost}
        duration={analysisData.duration}
        promptName={analysisData.promptName}
        analysisType={analysisData.analysisType}
        promptType={analysisData.promptType}
        templateName={analysisData.templateName}
        templateType={analysisData.templateType}
        analyzedBy={data.document_metadata?.analyzed_by || analysisData.analyzedBy}
        analysisDate={data.document_metadata?.analysis_timestamp || analysisData.analysisDate}
      />

      {/* Executive Summary */}
      {data.executive_summary && (
        <ExecutiveSummary data={data.executive_summary} documentType={documentType} />
      )}

      {/* Concepts Covered */}
      {data.concepts_covered && data.concepts_covered.length > 0 && (
        <ConceptsCovered concepts={data.concepts_covered} documentType={documentType} />
      )}

      {/* Learning Objectives */}
      {data.learning_objectives && data.learning_objectives.length > 0 && (
        <LearningObjectives objectives={data.learning_objectives} />
      )}

      {/* Learning Objectives Chart - coverage depth if available, otherwise points */}
      {data.learning_objectives && data.learning_objectives.some((obj: any) => obj.coverage_depth) ? (
        <LearningObjectivesCoverageChart
          objectives={data.learning_objectives}
          documentCount={documentCount}
        />
      ) : data.learning_objectives && data.learning_objectives.some((obj: any) => obj.points_allocated) ? (
        <LearningObjectivesPointsChart
          objectives={data.learning_objectives}
          documentCount={documentCount}
        />
      ) : null}


      {/* Problem-by-Problem Topic Breakdown - for exams only */}
      {(() => {
        console.log('  - data.problem_topic_breakdown exists?', !!data.problem_topic_breakdown);
        console.log('  - Condition result:', !!(data.problem_topic_breakdown && data.problem_topic_breakdown.length > 0));
        return null;
      })()}
      {data.problem_topic_breakdown && data.problem_topic_breakdown.length > 0 && (
        <ProblemTopicBreakdown breakdown={data.problem_topic_breakdown} documentType={documentType} />
      )}

      {/* Problem Analysis - for exams/assignments */}
      {data.problem_analysis && data.problem_analysis.length > 0 && (
        <ProblemAnalysis problems={data.problem_analysis} documentType={documentType} />
      )}

      {/* Pedagogical Approach */}
      {data.pedagogical_approach && (
        <PedagogicalApproach data={data.pedagogical_approach} />
      )}

      {/* Overall Assessment */}
      {data.overall_assessment && (
        <OverallAssessment data={data.overall_assessment} documentType={documentType} />
      )}

      {/* Raw Data (for debugging) - Hidden in PDF export */}
      <div className="pdf-hide">
        <RawDataDebug data={data} />
      </div>
    </div>
  );
}
